import { RippleButton } from "./RippleButton";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "../../../Components/InputFields/Select";
import SVG from "../../../Components/SVG";
import { API_BASE_URL } from "../../../config/api";

const cardClasses = `
    w-full max-w-[30rem] backdrop-blur-[14px] mx-auto rounded-2xl min-w-0 min-h-0 max-h-full h-fit p-3 sm:p-5 flex flex-col md:flex-row transition-all duration-300
    bg-zi-bg2-light dark:bg-zi-bg2-dark border border-transparent overflow-y-hidden
  `;
const buttonClasses = `
     bg-zi-gradient-button-blue
     w-full py-1 rounded-xl font-medium text-base transition  mt-3
  `;
const GATEWAYS_URL = `${API_BASE_URL}payment-payout/gateways/`;
const PAY_URL = `${API_BASE_URL}payment-payout/pay/`;
const PAY_GATEWAY_URL = `${API_BASE_URL}payment-payout/nowpayments/checkout/`;
const PAY_GATEWAY_URL_IRR = `${API_BASE_URL}payment-payout/directpay/checkout/`;
const REFRESH_URL = `${API_BASE_URL}auth/trader/jwt/refresh/`;

const resolvePaymentErrorMessage = (errorData) =>
  errorData?.detail ||
  errorData?.message ||
  errorData?.error ||
  "ثبت پرداخت با خطا مواجه شد. لطفا دوباره تلاش کنید.";

function PaymentErrorAlert({ message }) {
  if (!message) return null;

  return (
    <div
      role="alert"
      className="mt-4 flex items-start gap-2 rounded-xl border border-red-500/45 bg-red-500/10 px-3 py-2.5 text-right text-red-700 shadow-sm shadow-red-950/5 dark:text-red-200 dark:bg-red-500/15"
    >
      <SVG
        name="danger"
        classname="mt-0.5 size-5 shrink-0 fill-red-600 stroke-white dark:fill-red-400 dark:stroke-red-950"
      />
      <p className="text-xs leading-6 sm:text-sm">{message}</p>
    </div>
  );
}

export default function PaymentMethod({ plan, payablePrice, payablePriceIrr }) {
  const navigate = useNavigate();

  const trainingOptions = [
    {
      id: "crypto-gateway",
      title: "درگاه پرداخت ارز دیجیتال",
      description: "فعال‌سازی آنی حساب",
      svg: "nowPayment",
    },
    {
      id: "rial-gateway",
      title: "درگاه پرداخت ریالی",
      description: "فعال‌سازی آنی حساب",
      svg: "shaparak",
    },
    {
      id: "crypto-address",
      title: "کیف پول ارز دیجیتال",
      description: "فعال‌سازی حساب حداکثر ۸ ساعت",
      svg: "cryptoWallet",
    },
    // { id: "rial-gateway", title: "درگاه پرداخت ریالی", description: "فعال‌سازی آنی حساب" },
    // { id: "rial-address", title: "حساب به حساب ریالی", description: "فعال‌سازی حساب حداکثر ۸ ساعت", svg: "rialWallet" },
  ];

  const [selectedOption, setSelectedOption] = useState(trainingOptions[0].id);
  const [gatewayMap, setGatewayMap] = useState({});
  const [walletOptions, setWalletOptions] = useState({});
  const [transactionId, setTransactionId] = useState("");
  const [coin, setCoin] = useState("USDT");
  const [network, setNetwork] = useState("");
  const [copied, setCopied] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingGateway, setIsSubmittingGateway] = useState(false);
  const selectedGateway = network ? (gatewayMap[network] ?? null) : null;
  const selectedGatewayCode = selectedGateway?.code ?? "";
  const selectedWallet = network ? (walletOptions[network] ?? "") : "";
  const networkOptions = Object.keys(walletOptions);
  const planName = plan?.planName ?? "-";
  const displayPayablePrice = payablePrice === "" ? "-" : payablePrice;
  const displayPayablePriceIrr =
    payablePriceIrr === "" ? "-" : `${payablePriceIrr.toLocaleString()} ریال`;

  const logout = useCallback(() => {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    navigate("/signin");
  }, [navigate]);

  const refreshAccessToken = useCallback(async () => {
    const refreshResponse = await fetch(REFRESH_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logout();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  }, [logout]);

  const fetchWithAuthRetry = useCallback(
    async (url, options = {}) => {
      const getRequestOptions = () => ({
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(options.headers ?? {}),
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });

      let response = await fetch(url, getRequestOptions());

      if (response.status !== 401) {
        return response;
      }

      const nextAccessToken = await refreshAccessToken();
      if (!nextAccessToken) {
        return null;
      }

      return fetch(url, getRequestOptions());
    },
    [refreshAccessToken],
  );

  useEffect(() => {
    const fetchGateways = async () => {
      try {
        const response = await fetchWithAuthRetry(GATEWAYS_URL, {
          method: "GET",
        });
        if (!response) {
          return;
        }

        if (!response.ok) {
          throw new Error(`https error! Status: ${response.status}`);
        }

        const data = await response.json();
        const gateways = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        const nextGatewayMap = gateways.reduce((options, gateway) => {
          if (gateway?.name) {
            options[gateway.name] = {
              code: gateway.code ?? "",
              address: gateway.address ?? "",
            };
          }
          return options;
        }, {});
        const nextWalletOptions = gateways.reduce((options, gateway) => {
          if (gateway?.name && gateway?.address) {
            options[gateway.name] = gateway.address;
          }
          return options;
        }, {});

        setGatewayMap(nextGatewayMap);
        setWalletOptions(nextWalletOptions);
        setNetwork((currentNetwork) =>
          currentNetwork && nextGatewayMap[currentNetwork]
            ? currentNetwork
            : (Object.keys(nextGatewayMap)[0] ?? ""),
        );
      } catch (error) {
        console.error("Error fetching payment gateways:", error);
      }
    };

    fetchGateways();
  }, [fetchWithAuthRetry]);

  useEffect(() => {
    setCopied(false);
  }, [selectedWallet]);

  const handleCopyWallet = () => {
    if (!selectedWallet || !navigator?.clipboard?.writeText) return;

    navigator.clipboard
      .writeText(String(selectedWallet))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch((err) => console.error("Failed to copy wallet address:", err));
  };

  const handleSubmitPayment = async () => {
    const trimmedTransactionId = transactionId.trim();

    if (!selectedGatewayCode || !trimmedTransactionId) {
      setValidationError(
        "انتخاب شبکه و وارد کردن کد پیگیری پرداخت الزامی است.",
      );
      setSubmitError("");
      return;
    }

    setValidationError("");
    setSubmitError("");
    setIsSubmitting(true);

    try {
      const response = await fetchWithAuthRetry(PAY_URL, {
        method: "POST",
        body: JSON.stringify({
          payment_gateway_code: selectedGatewayCode,
          transaction_id: trimmedTransactionId,
        }),
      });

      if (!response) {
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        setSubmitError(resolvePaymentErrorMessage(errorData));
        return;
      }

      navigate("/purchase/finalized/manual");
    } catch (error) {
      console.error("Error submitting payment:", error);
      setSubmitError("ثبت پرداخت با خطا مواجه شد. لطفا دوباره تلاش کنید.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSubmitPaymentGateway = async () => {
    setValidationError("");
    setSubmitError("");
    setIsSubmittingGateway(true);

    try {
      const response = await fetchWithAuthRetry(
        selectedOption === trainingOptions[1].id
          ? PAY_GATEWAY_URL_IRR
          : PAY_GATEWAY_URL,
        {
          method: "POST",
        },
      );

      if (!response) {
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        setSubmitError(resolvePaymentErrorMessage(errorData));
        return;
      }

      const data = await response.json().catch(() => null);
      const checkoutUrl =
        data?.checkout_url || data?.data?.checkout_url || data?.url;

      if (!checkoutUrl) {
        setSubmitError("لینک درگاه پرداخت دریافت نشد. لطفا دوباره تلاش کنید.");
        return;
      }

      window.location.href = checkoutUrl;
    } catch (error) {
      console.error("Error submitting payment:", error);
      setSubmitError("ثبت پرداخت با خطا مواجه شد. لطفا دوباره تلاش کنید.");
    } finally {
      setIsSubmittingGateway(false);
    }
  };
  return (
    <main className="relative w-full max-w-5xl flex flex-col items-center mx-auto flex-1 min-h-0 overflow-y-hidden">
      <div className="w-full flex flex-col flex-1 min-h-0 justify-start pt-0">
        <div className={cardClasses.trim()}>
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="pb-2">
              <p className="text-2xl font-thin text-center w-full text-zi-text-light/50 dark:text-zi-text-dark/50">
                روش پرداخت
              </p>
            </div>

            <div className="hidden card-divider w-full h-px my-3 bg-zi-border-dark/50 dark:bg-zi-border-light/50" />

            <div className="w-full flex flex-col justify-center items-center gap-1 mb-2">
              <div className="flex items-center justify-between gap-2 w-full">
                <span className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
                  چالش انتخاب شده
                </span>
                <span className="text-lg font-extralight text-zi-text-light dark:text-zi-text-dark">
                  {planName}
                </span>
              </div>

              <div className="flex items-center justify-between gap-2 w-full">
                <span className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
                  مبلغ قابل پرداخت
                </span>
                <span className="text-lg font-extralight text-zi-text-light dark:text-zi-text-dark">
                  {selectedOption === trainingOptions[1].id
                    ? displayPayablePriceIrr
                    : displayPayablePrice === "-"
                      ? displayPayablePrice
                      : `$${displayPayablePrice}`}
                </span>
              </div>
              <div className=" card-divider w-full h-px bg-zi-border-dark/50 dark:bg-zi-border-light/50" />
            </div>

            {/* <p className="font-light text-xs text-zi-text-light dark:text-zi-text-dark mb-6">
                  توجه: در زمان قطعی اینترنت بین‌الملل، درگاه پرداخت ارز دیجیتال در دسترس نیست.
                </p> */}
            <div className="flex-1 overflow-y-auto">
              <p className="font-light text-xs text-zi-text-light dark:text-zi-text-dark mb-2">
                روش پرداخت را انتخاب کنید:
              </p>

              <div className="flex flex-col gap-2">
                {trainingOptions.map((option) => {
                  const isSelected = option.id === selectedOption;
                  return (
                    <label
                      key={option.id}
                      className={`w-full flex items-center justify-between gap-4 rounded-2xl border px-2.5 py-1.5 cursor-pointer transition-colors ${
                        isSelected
                          ? "border-zi-border-dark bg-zi-bg2-dark/5 dark:bg-zi-bg2-light/5"
                          : "border-zi-border-dark/50 hocus:border-zi-border-dark"
                      }`}
                    >
                      <div className="flex items-center gap-3 text-right">
                        <span
                          className={`size-4 rounded-full border-2 ${
                            isSelected
                              ? "border-zi-border-dark bg-zi-border-dark"
                              : "border-zi-border-dark"
                          }`}
                        />
                        <div>
                          <p className="text-sm sm:text-base font-medium text-zi-text-light dark:text-zi-text-dark whitespace-nowrap">
                            {option.title}
                          </p>
                          <p className="text-xs text-zi-text-light/60 dark:text-zi-text-dark/60 whitespace-nowrap">
                            {option.description}
                          </p>
                        </div>
                      </div>
                      <input
                        type="radio"
                        name="demoTrainingOption"
                        value={option.id}
                        checked={isSelected}
                        onChange={() => {
                          setSelectedOption(option.id);
                          setValidationError("");
                          setSubmitError("");
                        }}
                        className="sr-only"
                      />
                      <SVG name={option.svg} classname="" />
                    </label>
                  );
                })}
              </div>

              {selectedOption === trainingOptions[2].id && (
                <>
                  <p className="font-light text-xs text-zi-text-light dark:text-zi-text-dark mt-3 w-full">
                    ابتدا رمز ارز و شبکه مورد نظر را انتخاب کنید تا آدرس کیف پول
                    ایجاد گردد.
                  </p>

                  <div className="flex items-center gap-2.5 mt-2">
                    <div className="w-full flex flex-col gap-0.5">
                      <span className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
                        رمز ارز
                      </span>
                      <Select
                        options={["USDT"]}
                        value={coin}
                        onChange={(e) => {
                          setCoin(e.target.value);
                        }}
                      />
                    </div>
                    <div className="w-full flex flex-col gap-0.5">
                      <span className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
                        شبکه
                      </span>
                      <Select
                        options={networkOptions}
                        value={network}
                        onChange={(e) => {
                          setNetwork(e.target.value);
                          setValidationError("");
                          setSubmitError("");
                        }}
                      />
                    </div>
                  </div>

                  <div className="w-full flex flex-col gap-0.5 mt-2">
                    <span className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
                      آدرس کیف پول
                    </span>
                    <div className="relative w-full">
                      <input
                        className="bg-transparent py-1 pl-2.5 pr-9 border border-zi-border-dark rounded-lg w-full outline-none hocus:ring-1 hocus:ring-inset hocus:ring-zi-border-dark [direction:ltr] text-zi-text-light dark:text-zi-text-dark"
                        readOnly={true}
                        value={selectedWallet}
                      />
                      <button
                        type="button"
                        onClick={handleCopyWallet}
                        className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center"
                        aria-label="کپی آدرس کیف پول"
                      >
                        {selectedWallet &&
                          (copied ? (
                            <SVG
                              name="tick"
                              classname="size-4 fill-zi-text-dark stroke-zi-blue-light"
                            />
                          ) : (
                            <SVG
                              name="copy"
                              classname="size-4 stroke-zi-text-light dark:stroke-zi-text-dark cursor-pointer"
                            />
                          ))}
                      </button>
                    </div>
                  </div>

                  <p className="font-light text-xs text-zi-text-light dark:text-zi-text-dark mt-3">
                    پس از تکمیل پرداخت به آدرس کیف پول، کد پیگیری پرداخت را در
                    قسمت زیر وارد کنید.
                  </p>

                  <div className="w-full flex flex-col gap-0.5 mt-2">
                    <span className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
                      کد پیگیری پرداخت
                    </span>
                    <input
                      value={transactionId}
                      onChange={(e) => {
                        setTransactionId(e.target.value);
                        setValidationError("");
                        setSubmitError("");
                      }}
                      className="bg-transparent py-1 px-2.5 border border-zi-border-dark rounded-lg w-full outline-none hocus:ring-1 hocus:ring-inset hocus:ring-zi-border-dark [direction:ltr] text-zi-text-light dark:text-zi-text-dark"
                    />
                  </div>
                  <PaymentErrorAlert message={validationError || submitError} />

                  <p className="font-light text-xs text-zi-text-light dark:text-zi-text-dark mt-3">
                    در نهایت، برروی دکمه تأیید پرداخت بزنید. حداکثر ظرف ۸ ساعت
                    حساب موردنظر برای شما فعال می‌گردد.
                  </p>
                  <RippleButton
                    className={buttonClasses.trim()}
                    onClick={handleSubmitPayment}
                    disabled={isSubmitting}
                    ruleAgree={false}
                  >
                    {isSubmitting ? "در حال ثبت..." : "تأیید پرداخت"}
                  </RippleButton>
                </>
              )}
              {(selectedOption === trainingOptions[0].id ||
                selectedOption === trainingOptions[1].id) && (
                <RippleButton
                  className={buttonClasses.trim()}
                  onClick={handleSubmitPaymentGateway}
                  disabled={isSubmittingGateway}
                  ruleAgree={false}
                >
                  {isSubmittingGateway
                    ? "در حال ثبت..."
                    : "انتقال به درگاه پرداخت"}
                </RippleButton>
              )}
              {selectedOption === trainingOptions[0].id && (
                <PaymentErrorAlert message={submitError} />
              )}
            </div>
          </div>

          <div className="hidden card-divider w-full h-px my-3 bg-zi-gradient-divider md:mb-0 md:mx-5 md:w-px md:h-auto md:self-stretch md:shrink-0 md:bg-zi-gradient-divider-vertical" />
        </div>
      </div>
    </main>
  );
}
